import React, { AnyActionArg, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Chip,
  Stack,
  Alert,
  Divider,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Checkbox,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { BglPrimaryButton, BglPrimaryOutlinedButton } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { useToast } from 'context/ToastContext';
import { BglBackdropLoader } from 'components/loading/BglOverlayLoader';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId, selectPilotList } from 'store/dataReducers/pilotsSlice';
import { Info, Folder, Component, AlignLeft, CaseSensitive, FolderKanban, ListTree, ArrowLeft, Plus, SquareStack, Trash2, Pilcrow, Upload, Users,Combine } from 'lucide-react';
import { getStudioTokens } from 'layouts/Theme/theme-tokens';
import { useThemeMode } from 'context/ThemeContext';
import { ClusterApis } from 'apis/clusterAPI';

// Cluster interface for edit mode
interface Cluster {
  id: string;
  name: string;
  reference: string;
  type: string;
  categories: string[];
  userCount: number | null;
  isActive: boolean;
}




// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
}));

const CategoryChip = styled(Box)<{ selected?: boolean,tokens: any }>(({ theme, selected,tokens }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 8px',
  borderRadius: 8,
  fontWeight: 500,
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
 
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.main : tokens.buttonPrimaryHover.main,
    border: selected ? `2px solid ${theme.palette.primary.main}` : `2px solid ${tokens.buttonPrimaryHover.main}`,
    '& *': {
      color: selected ? theme.palette.primary.contrastText : 'white !important',
    },
  },
}));

const UploadArea = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: 8,
  padding: theme.spacing(2),
  textAlign: 'center',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 150,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + '05',
  },
}));

const RuleBuilderArea = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: 8,
  padding: theme.spacing(2),
  minHeight: 200,
  backgroundColor: theme.palette.background.default,
}));

// UUID File Validation Function
export const validateUUIDFileFormat = async (
  file: File
): Promise<{ valid: boolean; message: string }> => {
  const name = file.name.toLowerCase();
  const text = await file.text();

  // ---------- TXT ----------
  if (name.endsWith(".txt")) {
    if (!text.trim()) {
      return { valid: false, message: "TXT file is empty" };
    }
    return { valid: true, message: "TXT file format is valid" };
  }

  // ---------- CSV ----------
  if (name.endsWith(".csv")) {
    const rows = text.split("\n");
    if (rows.length < 2) {
      return {
        valid: false,
        message: "CSV must have a header and at least one row",
      };
    }
    const headers = rows[0]
      .split(",")
      .map((h) => h.trim().toLowerCase());
    if (!headers.includes("uuid")) {
      return { valid: false, message: `CSV must include a "uuid" column` };
    }
    return { valid: true, message: "CSV file format is valid" };
  }

  // ---------- JSON ----------
  if (name.endsWith(".json")) {
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) {
        return {
          valid: false,
          message: "JSON must be an array",
        };
      }
      return { valid: true, message: "JSON file format is valid" };
    } catch (e) {
      return { valid: false, message: "Invalid JSON format" };
    }
  }

  // ---------- Unsupported ----------
  return {
    valid: false,
    message: "Unsupported file format. Only .txt, .csv, .json are allowed.",
  };
};

// Extract UUIDs from file
const extractUUIDsFromFile = async (file: File): Promise<string[]> => {
  const name = file.name.toLowerCase();
  const text = await file.text();

  if (name.endsWith(".txt")) {
    // TXT: one UUID per line
    return text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }

  if (name.endsWith(".csv")) {
    // CSV: extract from uuid column
    const rows = text.split("\n").filter((row) => row.trim());
    if (rows.length < 2) return [];
    
    const headers = rows[0].split(",").map((h) => h.trim().toLowerCase());
    const uuidIndex = headers.indexOf("uuid");
    
    if (uuidIndex === -1) return [];
    
    return rows
      .slice(1)
      .map((row) => {
        const values = row.split(",").map((v) => v.trim());
        return values[uuidIndex] || "";
      })
      .filter((uuid) => uuid.length > 0);
  }

  if (name.endsWith(".json")) {
    // JSON: array of UUIDs or array of objects with uuid property
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => {
            if (typeof item === "string") {
              return item.trim();
            } else if (typeof item === "object" && item !== null && "uuid" in item) {
              return String(item.uuid).trim();
            }
            return "";
          })
          .filter((uuid) => uuid.length > 0);
      }
    } catch (e) {
      return [];
    }
  }

  return [];
};

// Evaluator Type Constants
export const EVALUATOR_TYPE = {
  RATE: 'rate',
  BI_MONTHLY: 'bi-monthly'
} as const;

export type EvaluatorType = typeof EVALUATOR_TYPE[keyof typeof EVALUATOR_TYPE];

// Rate Plan Interface
export interface RatePlan {
  planNumber: string;
  planName: string;
}

// Rule Builder Types
export type RuleType = 'attribute' | 'cluster';
export type Operator = 'AND' | 'OR';

export interface RuleItem {
  id: string;
  type: 'rule';
  ruleType: RuleType;
  value: string; // Selected attribute or cluster name
  operator?: Operator; // Operator after this rule (if not last)
}

export interface GroupItem {
  id: string;
  type: 'group';
  items: RuleBuilderItem[]; // Nested rules and groups
  operator?: Operator; // Operator after this group (if not last)
}

export type RuleBuilderItem = RuleItem | GroupItem;

// Rule Item Component
interface AttributeOption {
  attributeId: string;
  attributeName: string;
}

interface RuleItemComponentProps {
  item: RuleBuilderItem;
  index: number;
  items: RuleBuilderItem[];
  clusters: string[];
  attributes: AttributeOption[];
  clustersLoading: boolean;
  attributesLoading: boolean;
  onUpdateRule: (updates: Partial<RuleItem>) => void;
  onUpdateGroup: (updates: Partial<GroupItem>) => void;
  onDelete: () => void;
  onAddRule: () => void;
  onAddGroup: () => void;
  level?: number;
  onError?: (message: string) => void;
}

const RuleItemComponent: React.FC<RuleItemComponentProps> = ({
  item,
  index,
  items,
  clusters,
  attributes,
  clustersLoading,
  attributesLoading,
  onUpdateRule,
  onUpdateGroup,
  onDelete,
  onAddRule,
  onAddGroup,
  level = 0,
  onError,
}) => {
  const theme = useTheme();

  if (item.type === 'rule') {
    const rule = item as RuleItem;
    const attributeOptions = rule.ruleType === 'attribute' ? attributes : [];
    const clusterOptions = rule.ruleType === 'cluster' ? clusters : [];
    const isLoading = rule.ruleType === 'attribute' ? attributesLoading : clustersLoading;

    return (
      <Box
        sx={{
          mb: 1.5,
          p: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          ml: level * 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
          <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
            <Select
              value={rule.ruleType}
              onChange={(e) => onUpdateRule({ ruleType: e.target.value as RuleType, value: '' })}
              sx={{ 
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 1,
                },
              }}
            >
              <MenuItem value="attribute">Attribute</MenuItem>
              <MenuItem value="cluster">Cluster</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
            <Select
              value={rule.value}
              onChange={(e) => {
                const newValue = e.target.value;
                // Validate: only alphanumeric and underscore allowed, no spaces or special characters
                if (rule.ruleType === 'cluster' && newValue) {
                  const clusterRefRegex = /^[a-zA-Z0-9_]+$/;
                  if (!clusterRefRegex.test(newValue) || newValue.includes(' ')) {
                  if (onError) {
                      onError('Cluster reference can only contain letters, numbers, and underscores. No spaces or special characters allowed.');
                  }
                  return;
                  }
                }
                onUpdateRule({ value: newValue });
              }}
              displayEmpty
              disabled={isLoading || (rule.ruleType === 'attribute' ? attributeOptions.length === 0 : clusterOptions.length === 0)}
              sx={{ 
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 1,
                },
              }}
            >
              <MenuItem value="" disabled>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  {isLoading ? 'Loading...' : rule.ruleType === 'attribute' ? 'Select a attribute...' : `Select a ${rule.ruleType}...`}
                </Typography>
              </MenuItem>
              {rule.ruleType === 'attribute' 
                ? attributeOptions.map((attr) => (
                    <MenuItem key={attr.attributeId} value={attr.attributeId}>
                      {attr.attributeId}
                </MenuItem>
                  ))
                : clusterOptions.map((cluster) => (
                    <MenuItem key={cluster} value={cluster}>
                      {cluster}
                    </MenuItem>
                  ))
              }
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
              <Select
                value={rule.operator || 'AND'}
                onChange={(e) => onUpdateRule({ operator: e.target.value as Operator })}
              sx={{ 
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 1,
                },
              }}
              >
                <MenuItem value="AND">AND</MenuItem>
                <MenuItem value="OR">OR</MenuItem>
              </Select>
            </FormControl>

          <IconButton
            size="small"
            onClick={onDelete}
            sx={{ 
              color: 'error.main',
              flexShrink: 0,
              ml: 0.5,
            }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Box>
      </Box>
    );
  } else {
    const group = item as GroupItem;

    return (
      <Box
        sx={{
          mb: 1.5,
          p: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          bgcolor: 'background.paper',
          ml: level * 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Pilcrow size={14} />}
              onClick={() => onAddRule()}
              sx={{ 
                fontSize: '0.75rem', 
                py: 0.5,
                px: 1.5,
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                }
              }}
            >
              Add Rule
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Combine size={14} />}
              onClick={() => onAddGroup()}
              sx={{ 
                fontSize: '0.75rem', 
                py: 0.5,
                px: 1.5,
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                }
              }}
            >
              Add Group
            </Button>
            {index < items.length - 1 && (
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={group.operator || 'AND'}
                  onChange={(e) => onUpdateGroup({ operator: e.target.value as Operator })}
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="AND">AND</MenuItem>
                  <MenuItem value="OR">OR</MenuItem>
                </Select>
              </FormControl>
            )}
            
          </Box>
            <IconButton
              size="small"
              onClick={onDelete}
              sx={{ color: 'error.main', ml: 0.5 }}
            >
              <Trash2 size={16} />
            </IconButton>
        </Box>

        {group.items.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 3, 
            bgcolor: 'background.default', 
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              This group is empty. Add a rule to begin.
            </Typography>
          </Box>
        ) : (
          <Box>
            {group.items.map((nestedItem, nestedIndex) => (
              <RuleItemComponent
                key={nestedItem.id}
                item={nestedItem}
                index={nestedIndex}
                items={group.items}
                clusters={clusters}
                attributes={attributes}
                clustersLoading={clustersLoading}
                attributesLoading={attributesLoading}
                onUpdateRule={(updates) => {
                  const updatedItems = [...group.items];
                  updatedItems[nestedIndex] = { ...(updatedItems[nestedIndex] as RuleItem), ...updates };
                  onUpdateGroup({ items: updatedItems });
                }}
                onUpdateGroup={(updates) => {
                  const updatedItems = [...group.items];
                  updatedItems[nestedIndex] = { ...(updatedItems[nestedIndex] as GroupItem), ...updates };
                  onUpdateGroup({ items: updatedItems });
                }}
                onDelete={() => {
                  const updatedItems = group.items.filter((_, i) => i !== nestedIndex);
                  onUpdateGroup({ items: updatedItems });
                }}
                onAddRule={() => {
                  if (nestedItem.type === 'group') {
                    const nestedGroup = nestedItem as GroupItem;
                    const newRule: RuleItem = {
                      id: `rule-${Date.now()}`,
                      type: 'rule',
                      ruleType: 'attribute',
                      value: '',
                    };
                    const updatedItems = [...group.items];
                    updatedItems[nestedIndex] = {
                      ...nestedGroup,
                      items: [...nestedGroup.items, newRule],
                    };
                    onUpdateGroup({ items: updatedItems });
                  }
                }}
                onAddGroup={() => {
                  if (nestedItem.type === 'group') {
                    const nestedGroup = nestedItem as GroupItem;
                    const newGroup: GroupItem = {
                      id: `group-${Date.now()}`,
                      type: 'group',
                      items: [],
                    };
                    const updatedItems = [...group.items];
                    updatedItems[nestedIndex] = {
                      ...nestedGroup,
                      items: [...nestedGroup.items, newGroup],
                    };
                    onUpdateGroup({ items: updatedItems });
                  }
                }}
                level={level + 1}
                onError={onError}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }
};

// Category options

export const DCXClusterCreatePage: React.FC= () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const { mode } = useThemeMode();
  const tokens = getStudioTokens(mode);
  const toast = useToast();
  const location = useLocation();

  // Get selected utility/pilot from Redux
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  const selectedPilot = pilotList.find((pilot: any) => String(pilot.projectId) === String(selectedPilotId));

  // Get cluster ID from location state if editing
  const editClusterId = (location.state?.state as any)?.clusterId as string | undefined;
  const [clusterDetails, setClusterDetails] = useState<any>(null);
  const [loadingClusterDetails, setLoadingClusterDetails] = useState(false);
  const isEditMode = !!editClusterId;

  // Form state
  const [clusterReference, setClusterReference] = useState('');
  const [clusterName, setClusterName] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['program']);
  const [clusterType, setClusterType] = useState<'physical' | 'virtual'>('physical');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedUUIDs, setUploadedUUIDs] = useState<string[]>([]);
  const [fileValidationError, setFileValidationError] = useState<string>('');
  const [showUserCount, setShowUserCount] = useState(false);
  const [rules, setRules] = useState<RuleBuilderItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isInactive, setIsInactive] = useState(false);

  // Rule builder data
  const [clusters, setClusters] = useState<string[]>([]);
  const [attributes, setAttributes] = useState<Array<{ attributeId: string; attributeName: string }>>([]);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [physicalExperimentId, setPhysicalExperimentId] = useState<string>('');
  const [virtualExperimentId, setVirtualExperimentId] = useState<string>('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [attributeName, setAttributeName] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [evaluatorType, setEvaluatorType] = useState<EvaluatorType>(EVALUATOR_TYPE.RATE);
  const [days, setDays] = useState<number>(30);
  const [selectedRatePlans, setSelectedRatePlans] = useState<RatePlan[]>([]);
  const [ratePlansOptions, setRatePlansOptions] = useState<RatePlan[]>([]);
  const [ratePlansLoading, setRatePlansLoading] = useState(false);
  const [ratePlansError, setRatePlansError] = useState<string | null>(null);
  const [savingAttribute, setSavingAttribute] = useState(false);
  const [saveAttributeError, setSaveAttributeError] = useState<string | null>(null);

  // Fuel type options
  const fuelTypeOptions = [
    'Electric',
    'Gas',
    'Water',
  ];

  const chipStyle = (category: string)=>({
    color: selectedCategories.includes(category) ? theme.palette.primary.contrastText : theme.palette.secondary.contrastText,
    marginRight:4
  })

  const categoryOptions = [
    { id: 'program', label: 'Program', icon: <Folder size={16} style={chipStyle('program')}/>, description: 'A Program Cluster is used to group customers that are participating in a specific program (for example an Energy Efficiency Treatment or Control group).' },
    { id: 'feature', label: 'Feature', icon: <Component size={16} style={chipStyle('feature')}/>, description: 'A Feature Cluster is used to enable or disable specific application features for a defined group of customers.' },
    { id: 'template', label: 'Template', icon: <AlignLeft size={16} style={chipStyle('template')}/>, description: 'A Template Cluster is used to apply different content templates (like email layouts) to different customer groups.' },
    { id: 'content', label: 'Content', icon: <CaseSensitive size={16} style={chipStyle('content')}/>, description: 'A Content Cluster is used for fine-grained content variations, often for A/B testing messages or offers.' }
  ];
  

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setFileValidationError('');
      
      // Validate file format
      const validation = await validateUUIDFileFormat(file);
      
      if (!validation.valid) {
        setFileValidationError(validation.message);
        setUploadedFile(null);
        setUploadedUUIDs([]);
        setShowUserCount(false);
        toast.error(validation.message);
        return;
      }

      // Extract UUIDs from file
      const uuids = await extractUUIDsFromFile(file);
      
      if (uuids.length === 0) {
        setFileValidationError('No UUIDs found in the file');
        setUploadedFile(null);
        setUploadedUUIDs([]);
        setShowUserCount(false);
        toast.error('No UUIDs found in the file');
        return;
      }

      setUploadedFile(file);
      setUploadedUUIDs(uuids);
      setShowUserCount(false); // Reset count display when new file is uploaded
      toast.success(`File validated successfully. Found ${uuids.length} UUID(s).`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      setFileValidationError(errorMessage);
      setUploadedFile(null);
      setUploadedUUIDs([]);
      setShowUserCount(false);
      toast.error(errorMessage);
    }
  };

  // Fetch clusters and attributes
  const fetchClustersAndAttributes = async () => {
    if (!selectedPilot?.projectId) return;

    const projectId = selectedPilot.projectId.toString();
    const apiHost = (selectedPilot as any).api_host || undefined;

    // Fetch clusters
    setClustersLoading(true);
    try {
      const clustersResponse = await ClusterApis.getClusters(rc, projectId);
      if (!clustersResponse.success || clustersResponse.error) {
        rc.error(rc.getName(DCXClusterCreatePage), 'Error fetching clusters:', clustersResponse.error);
        setClusters([]);
      } else {
        const clustersData: any = clustersResponse.data;
        
        // New response structure: { physical_experimentId, virtual_experimentId, clusters: [...] }
        if (clustersData && typeof clustersData === 'object' && !Array.isArray(clustersData)) {
          // Extract experiment IDs
          if (clustersData.physical_experimentId) {
            setPhysicalExperimentId(clustersData.physical_experimentId);
          }
          if (clustersData.virtual_experimentId) {
            setVirtualExperimentId(clustersData.virtual_experimentId);
          }
          
          // Extract clusters array and filter only physical clusters
          let allClusters: any[] = [];
          if (clustersData.clusters && Array.isArray(clustersData.clusters)) {
            allClusters = clustersData.clusters;
          } else if (clustersData.data && Array.isArray(clustersData.data)) {
            allClusters = clustersData.data;
          } else if (clustersData.payload && Array.isArray(clustersData.payload)) {
            allClusters = clustersData.payload;
          }
          
          // Filter only physical clusters for rule builder
          const physicalClusters = allClusters.filter((c: any) => {
            const clusterCategory = c.clusterCategory || '';
            return clusterCategory.toString().toUpperCase() === 'PHYSICAL';
          });
          const clusterReferences = physicalClusters.map((c: any) => c.clusterReference).filter(Boolean);
          setClusters(clusterReferences);
        } else if (Array.isArray(clustersData)) {
          // Fallback: handle direct array response (old format)
          // Filter only physical clusters
          const physicalClusters = clustersData.filter((c: any) => {
            const clusterCategory = c.clusterCategory || '';
            return clusterCategory.toString().toUpperCase() === 'PHYSICAL';
          });
          const clusterReferences = physicalClusters.map((c: any) => c.clusterReference).filter(Boolean);
          setClusters(clusterReferences);
        } else {
          setClusters([]);
        }
      }
    } catch (error) {
      rc.error(rc.getName(DCXClusterCreatePage), 'Error fetching clusters:', error);
      setClusters([]);
    } finally {
      setClustersLoading(false);
    }

    // Fetch attributes
    setAttributesLoading(true);
    try {
      const attributesResponse = await ClusterApis.getAttributes(rc, projectId);
      if (!attributesResponse.success || attributesResponse.error) {
        rc.error(rc.getName(DCXClusterCreatePage), 'Error fetching attributes:', attributesResponse.error);
        setAttributes([]);
      } else {
        const attributesData = attributesResponse.data;
        // Attributes response has structure: { "data": [...] } or direct array
        if (attributesData && typeof attributesData === 'object' && 'data' in attributesData && Array.isArray((attributesData as any).data)) {
          const attributeList = (attributesData as any).data
            .map((a: any) => ({
              attributeId: a.attributeId || '',
              attributeName: a.attributeName || ''
            }))
            .filter((a: any) => a.attributeId && a.attributeName);
          setAttributes(attributeList);
        } else if (Array.isArray(attributesData)) {
          // Handle direct array response
          const attributeList = attributesData
            .map((a: any) => ({
              attributeId: a.attributeId || '',
              attributeName: a.attributeName || ''
            }))
            .filter((a: any) => a.attributeId && a.attributeName);
          setAttributes(attributeList);
        } else {
          setAttributes([]);
        }
      }
    } catch (error) {
      rc.error(rc.getName(DCXClusterCreatePage), 'Error fetching attributes:', error);
      setAttributes([]);
    } finally {
      setAttributesLoading(false);
    }
  };

  // Parse evaluation criteria back to rules structure
  const parseEvaluationCriteria = React.useCallback((criteria: string): RuleBuilderItem[] => {
    if (!criteria || !criteria.trim()) {
      return [];
    }

    try {
      // Replace && with AND and || with OR for parsing
      const normalizedCriteria = criteria
        .replace(/\s*&&\s*/g, ' AND ')
        .replace(/\s*\|\|\s*/g, ' OR ')
        .trim();

      // Use a recursive descent parser
      let index = 0;
      const tokens = normalizedCriteria.match(/(\(|\)|AND|OR|[^\s()]+)/g) || [];
      
      const parseExpression = (): RuleBuilderItem[] => {
        const items: RuleBuilderItem[] = [];
        
        while (index < tokens.length) {
          const token = tokens[index];
          
          if (token === '(') {
            index++; // Skip opening paren
            const groupItems = parseExpression();
            if (groupItems.length > 0) {
              const group: GroupItem = {
                id: `group-${Date.now()}-${Math.random()}`,
                type: 'group',
                items: groupItems
              };
              items.push(group);
            }
          } else if (token === ')') {
            index++; // Skip closing paren
            break;
          } else if (token === 'AND' || token === 'OR') {
            if (items.length > 0) {
              const lastItem = items[items.length - 1];
              if (lastItem.type === 'rule') {
                (lastItem as RuleItem).operator = token as Operator;
              } else if (lastItem.type === 'group') {
                (lastItem as GroupItem).operator = token as Operator;
              }
            }
            index++;
          } else {
            // This is a value (attribute or cluster)
            const value = token.trim();
            let ruleType: RuleType = 'attribute';
            let ruleValue = value;
            
            // Check if it's a cluster reference
            if (value.startsWith('CLUSTER.')) {
              ruleType = 'cluster';
              ruleValue = value.replace(/^CLUSTER\./, '');
            } else if (value.startsWith('GLOBAL.') || value.startsWith('PILOT.')) {
              ruleType = 'attribute';
              ruleValue = value;
            } else {
              // Try to determine if it's a cluster or attribute
              // If it's in the clusters list, it's a cluster
              if (clusters.includes(value)) {
                ruleType = 'cluster';
                ruleValue = value;
              } else {
                ruleType = 'attribute';
                ruleValue = value;
              }
            }
            
            const rule: RuleItem = {
              id: `rule-${Date.now()}-${Math.random()}`,
              type: 'rule',
              ruleType,
              value: ruleValue
            };
            items.push(rule);
            index++;
          }
        }
        
        return items;
      };
      
      return parseExpression();
    } catch (error) {
      console.error('Error parsing evaluation criteria:', error);
      return [];
    }
  }, [clusters]);

  // Fetch cluster details when in edit mode
  useEffect(() => {
    const fetchClusterDetails = async () => {
      if (editClusterId && selectedPilot?.projectId) {
        setLoadingClusterDetails(true);
        try {
          const response = await ClusterApis.getClusterDetails(rc, selectedPilot.projectId, editClusterId);
          
          if (response.success && response.data) {
            const responseData = response.data;
            // Handle nested structure: data.payload.clusterMetaData
            const clusterMetaData = responseData.data?.payload?.clusterMetaData;
            
            if (clusterMetaData) {
              setClusterDetails(clusterMetaData);
              
              // Prefill basic fields
              setClusterReference(clusterMetaData.clusterReference || '');
              setClusterName(clusterMetaData.clusterName || '');
              
              // Handle cluster category - can be string or array
              let categories: string[] = [];
              if (clusterMetaData.clusterCategory) {
                if (Array.isArray(clusterMetaData.clusterCategory)) {
                  categories = clusterMetaData.clusterCategory;
                } else {
                  // Single category string, convert to array
                  categories = [clusterMetaData.clusterCategory];
                }
              }
              // Normalize categories to lowercase for matching
              const normalizedCategories = categories.map((cat: string) => 
                cat.toLowerCase()
              );
              setSelectedCategories(normalizedCategories.length > 0 ? normalizedCategories : ['program']);
              
              // Determine cluster type based on evaluationCriteria (virtual) or uuid/uuids (physical)
              const hasEvaluationCriteria = !!clusterMetaData.evaluationCriteria;
              const hasUuids = !!(clusterMetaData.uuids && Array.isArray(clusterMetaData.uuids) && clusterMetaData.uuids.length > 0);
              
              if (hasEvaluationCriteria) {
                setClusterType('virtual');
              } else if (hasUuids || clusterMetaData.uuid) {
                setClusterType('physical');
                // For physical clusters, set UUIDs if available
                if (clusterMetaData.uuids && Array.isArray(clusterMetaData.uuids)) {
                  setUploadedUUIDs(clusterMetaData.uuids);
                }
              } else {
                // Default to physical if we can't determine
                setClusterType('physical');
              }
              
              // Set inactive status (active is inverse of inactive)
              setIsInactive(!clusterMetaData.active);
              
              // For virtual clusters, evaluation criteria will be parsed in another useEffect
              // once clusters and attributes are loaded
            } else {
              toast.error('Cluster details not found in response');
            }
          } else {
            toast.error(response.error || 'Failed to fetch cluster details');
          }
        } catch (error) {
          rc.error(rc.getName(DCXClusterCreatePage), 'Error fetching cluster details:', error);
          toast.error('Failed to fetch cluster details');
        } finally {
          setLoadingClusterDetails(false);
        }
      }
    };

    fetchClusterDetails();
  }, [editClusterId, selectedPilot?.projectId, rc]);

  // Parse evaluation criteria to rules once clusters and attributes are loaded
  useEffect(() => {
    if (clusterDetails && clusterType === 'virtual' && clusterDetails.evaluationCriteria && 
        clusters.length > 0 && attributes.length > 0) {
      try {
        const parsedRules = parseEvaluationCriteria(clusterDetails.evaluationCriteria);
        if (parsedRules.length > 0) {
          setRules(parsedRules);
        }
      } catch (error) {
        console.error('Error parsing evaluation criteria:', error);
        toast.error('Failed to parse evaluation criteria');
      }
    }
  }, [clusterDetails, clusterType, clusters, attributes, parseEvaluationCriteria]);

  // Refresh attributes after creating a new one
  React.useEffect(() => {
    console.log('modalOpen', modalOpen);
    console.log('selectedPilot?.projectId', selectedPilot?.projectId);
    if (selectedPilot?.projectId && !modalOpen) {
      fetchClustersAndAttributes();
    }
  }, [modalOpen, selectedPilot?.projectId]);

  const handleAddRule = (parentItems?: RuleBuilderItem[], parentIndex?: number) => {
    const newRule: RuleItem = {
      id: `rule-${Date.now()}`,
      type: 'rule',
      ruleType: 'attribute',
      value: '',
    };

    if (parentItems !== undefined && parentIndex !== undefined) {
      // Add to nested group
      const updatedItems = [...parentItems];
      const group = updatedItems[parentIndex] as GroupItem;
      updatedItems[parentIndex] = {
        ...group,
        items: [...group.items, newRule],
      };
      setRules(updatedItems);
    } else {
      // Add to root level
      setRules([...rules, newRule]);
    }
  };

  const handleAddGroup = (parentItems?: RuleBuilderItem[], parentIndex?: number) => {
    const newGroup: GroupItem = {
      id: `group-${Date.now()}`,
      type: 'group',
      items: [],
    };

    if (parentItems !== undefined && parentIndex !== undefined) {
      // Add to nested group
      const updatedItems = [...parentItems];
      const group = updatedItems[parentIndex] as GroupItem;
      updatedItems[parentIndex] = {
        ...group,
        items: [...group.items, newGroup],
      };
      setRules(updatedItems);
    } else {
      // Add to root level
      setRules([...rules, newGroup]);
    }
  };

  const handleDeleteItem = (index: number, parentItems?: RuleBuilderItem[], parentIndex?: number) => {
    if (parentItems !== undefined && parentIndex !== undefined) {
      // Delete from nested group
      const updatedItems = [...parentItems];
      const group = updatedItems[parentIndex] as GroupItem;
      const newGroupItems = group.items.filter((_, i) => i !== index);
      updatedItems[parentIndex] = {
        ...group,
        items: newGroupItems,
      };
      setRules(updatedItems);
    } else {
      // Delete from root level
      setRules(rules.filter((_, i) => i !== index));
    }
  };

  const handleUpdateRule = (
    index: number,
    updates: Partial<RuleItem>,
    parentItems?: RuleBuilderItem[],
    parentIndex?: number
  ) => {
    if (parentItems !== undefined && parentIndex !== undefined) {
      // Update in nested group
      const updatedItems = [...parentItems];
      const group = updatedItems[parentIndex] as GroupItem;
      const updatedGroupItems = [...group.items];
      updatedGroupItems[index] = { ...(updatedGroupItems[index] as RuleItem), ...updates };
      updatedItems[parentIndex] = {
        ...group,
        items: updatedGroupItems,
      };
      setRules(updatedItems);
    } else {
      // Update at root level
      const updatedRules = [...rules];
      updatedRules[index] = { ...(updatedRules[index] as RuleItem), ...updates };
      setRules(updatedRules);
    }
  };

  const handleUpdateGroup = (
    index: number,
    updates: Partial<GroupItem>,
    parentItems?: RuleBuilderItem[],
    parentIndex?: number
  ) => {
    if (parentItems !== undefined && parentIndex !== undefined) {
      // Update nested group
      const updatedItems = [...parentItems];
      const group = updatedItems[parentIndex] as GroupItem;
      const updatedGroupItems = [...group.items];
      updatedGroupItems[index] = { ...(updatedGroupItems[index] as GroupItem), ...updates };
      updatedItems[parentIndex] = {
        ...group,
        items: updatedGroupItems,
      };
      setRules(updatedItems);
    } else {
      // Update at root level
      const updatedRules = [...rules];
      updatedRules[index] = { ...(updatedRules[index] as GroupItem), ...updates };
      setRules(updatedRules);
    }
  };

  // Validate rule builder items recursively
  const validateRuleBuilder = (items: RuleBuilderItem[]): { valid: boolean; message: string } => {
    if (items.length === 0) {
      return { valid: false, message: 'At least one rule or group is required' };
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type === 'rule') {
        const rule = item as RuleItem;
        // Rule must have a value selected
        if (!rule.value || !rule.value.trim()) {
          return { valid: false, message: 'All rules must have an attribute or cluster selected' };
        }
      } else if (item.type === 'group') {
        const group = item as GroupItem;
        // Group must have at least one item
        if (group.items.length === 0) {
          return { valid: false, message: 'All groups must contain at least one rule or group' };
        }
        // Recursively validate nested items
        const nestedValidation = validateRuleBuilder(group.items);
        if (!nestedValidation.valid) {
          return nestedValidation;
        }
      }
    }

    return { valid: true, message: '' };
  };

  const generateEvaluationCriteria = (items: RuleBuilderItem[]): string => {
    const parts: string[] = [];
    
    items.forEach((item, index) => {
      if (item.type === 'rule') {
        const rule = item as RuleItem;
        if (rule.value) {
          if (rule.ruleType === 'cluster') {
            // Use CLUSTER.CLUSTER_REFERENCE format (only add prefix if not already present)
            const clusterValue = rule.value.startsWith('CLUSTER.') ? rule.value : `CLUSTER.${rule.value}`;
            parts.push(clusterValue);
          } else {
            // Use attributeId as-is (it already contains the full path like GLOBAL.AMR_WATER)
            parts.push(rule.value);
          }
        }
      } else if (item.type === 'group') {
        const group = item as GroupItem;
        if (group.items.length > 0) {
          parts.push(`(${generateEvaluationCriteria(group.items)})`);
        }
      }

      // Add operator if not last item (default to 'AND' if not set)
      if (index < items.length - 1) {
        const operator = item.operator || 'AND';
        // Replace AND with && and OR with ||
        const operatorSymbol = operator === 'OR' ? '||' : '&&';
        parts.push(operatorSymbol);
      }
    });

    return parts.join(' ');
  };

  // Generate rule logic display
  const generateRuleLogic = (items: RuleBuilderItem[]): React.ReactNode[] => {
    const tokens: React.ReactNode[] = [];
    
    items.forEach((item, index) => {
      if (item.type === 'rule') {
        const rule = item as RuleItem;
        const label = rule.ruleType === 'attribute' ? 'Attr' : 'Cluster';
        const value = rule.value || '""';
        tokens.push(
          <Chip
            key={`${item.id}-token`}
            label={`${label}: ${value}`}
            size="small"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 500,
            }}
          />
        );
      } else if (item.type === 'group') {
        const group = item as GroupItem;
        tokens.push(
          <Box
            key={`${item.id}-open`}
            component="span"
            sx={{
              color: theme.palette.primary.main,
              fontSize: '1.2rem',
              fontWeight: 'bold',
            }}
          >
            (
          </Box>
        );
        if (group.items.length > 0) {
          tokens.push(...generateRuleLogic(group.items));
        }
        tokens.push(
          <Box
            key={`${item.id}-close`}
            component="span"
            sx={{
              color: theme.palette.primary.main,
              fontSize: '1.2rem',
              fontWeight: 'bold',
            }}
          >
            )
          </Box>
        );
      }

      // Add operator if not last item (default to 'AND' if not set)
      if (index < items.length - 1) {
        tokens.push(
          <Chip
            key={`${item.id}-op`}
            label={item.operator || 'AND'}
            size="small"
            sx={{
              bgcolor: theme.palette.success.main,
              color: 'white',
              fontWeight: 600,
            }}
          />
        );
      }
    });

    return tokens;
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Validate form
      if (!clusterReference.trim()) {
        const errorMsg = 'Cluster reference is required';
        setError(errorMsg);
        toast.error(errorMsg);
        setIsLoading(false);
        return;
      }

      // Validate cluster reference: only alphanumeric and underscore allowed, no spaces
      const clusterRefRegex = /^[a-zA-Z0-9_]+$/;
      if (!clusterRefRegex.test(clusterReference.trim())) {
        const errorMsg = 'Cluster reference can only contain letters, numbers, and underscores. No spaces or special characters allowed.';
        setError(errorMsg);
        toast.error(errorMsg);
        setIsLoading(false);
        return;
      }
      
      if (clusterReference.includes(' ')) {
        const errorMsg = 'Cluster reference cannot contain spaces';
        setError(errorMsg);
        toast.error(errorMsg);
        setIsLoading(false);
        return;
      }

      if (!clusterName.trim()) {
        const errorMsg = 'Cluster name is required';
        setError(errorMsg);
        toast.error(errorMsg);
        setIsLoading(false);
        return;
      }

      if (clusterType === 'physical') {
        if (!uploadedFile) {
          const errorMsg = 'File upload is required for Physical clusters';
          setError(errorMsg);
          toast.error(errorMsg);
        setIsLoading(false);
        return;
        }
        if (uploadedUUIDs.length === 0) {
          const errorMsg = 'No valid UUIDs found in the uploaded file';
          setError(errorMsg);
          toast.error(errorMsg);
          setIsLoading(false);
          return;
        }
      }

      if (clusterType === 'virtual') {
        if (rules.length === 0) {
          const errorMsg = 'At least one rule is required for Virtual clusters';
          setError(errorMsg);
          toast.error(errorMsg);
        setIsLoading(false);
        return;
        }
        
        // Validate that all rules and groups are complete
        const validation = validateRuleBuilder(rules);
        if (!validation.valid) {
          setError(validation.message);
          toast.error(validation.message);
          setIsLoading(false);
          return;
        }
      }

      // Get the appropriate experiment ID based on cluster type
      const experimentId = clusterType === 'physical' ? physicalExperimentId : virtualExperimentId;
      
      if (!experimentId) {
        const errorMsg = `Experiment ID not found for ${clusterType} clusters. Please refresh and try again.`;
        setError(errorMsg);
        toast.error(errorMsg);
        setIsLoading(false);
        return;
      }

      // Generate evaluation criteria for virtual clusters
      let evaluationCriteria = '';
      if (clusterType === 'virtual') {
        evaluationCriteria = generateEvaluationCriteria(rules);
        if (!evaluationCriteria.trim()) {
          const errorMsg = 'Invalid evaluation criteria. Please check your rules.';
          setError(errorMsg);
          toast.error(errorMsg);
          setIsLoading(false);
          return;
        }
      }

      // Prepare request data according to API specification
      const requestData: any = {
        experimentId: experimentId,
        projectId: selectedPilot?.projectId?.toString() || '',
        clusterName: clusterName.trim(),
        clusterType: clusterType.toUpperCase(),
        clusterCategory: selectedCategories.map(cat => cat.toUpperCase()), // Send as array
        clusterReference: clusterReference.trim(),
        control: false,
        email: false,
        paper: false,
        sms: false,
        userMovementAllowed: false,
        userAssignmentAllowed: false
      };

      // Include UUIDs for physical clusters
      if (clusterType === 'physical' && uploadedUUIDs.length > 0) {
        requestData.uuids = uploadedUUIDs;
      }

      // Only include evaluationCriteria for virtual clusters
      if (clusterType === 'virtual' && evaluationCriteria) {
        requestData.evaluationCriteria = evaluationCriteria;
      }

      // For Virtual clusters in edit mode, use the update API
      if (isEditMode && clusterType === 'virtual' && editClusterId) {
        const updateRequestData = {
          cluster_categories: selectedCategories.map(cat => cat.toUpperCase()),
          cluster_name: clusterName.trim(),
          evaluation_criteria: evaluationCriteria,
          active: !isInactive
        };

        const updateResponse = await ClusterApis.updateCluster(rc, editClusterId, updateRequestData);

        if (!updateResponse.success || updateResponse.error) {
          const errorMessage = updateResponse.error?.message || updateResponse.error || 'Failed to update cluster. Please try again.';
          setError(errorMessage);
          toast.error(errorMessage);
          return;
        }

        toast.success('Cluster updated successfully');
        
        // Navigate back to clusters list
        rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS);
        return;
      }

      // Call the API to create cluster
      const response = await ClusterApis.createCluster(rc, requestData);

      if (!response.success || response.error) {
        const errorMessage = response.error?.message || response.error || 'Failed to create cluster. Please try again.';
        setError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      toast.success('Cluster created successfully');
      
      // Navigate back to clusters list
      rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create cluster. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS);
  };

  const fetchRatePlans = async () => {
    try {
      setRatePlansLoading(true);
      setRatePlansError(null);
      
      // Get utility ID from selected pilot
      if (!selectedPilot || !selectedPilot.projectId) {
        setRatePlansError('Please select a utility/pilot to fetch rate plans');
        setRatePlansOptions([]);
        setRatePlansLoading(false);
        return;
      }

      const apiHost = (selectedPilot as any).api_host || undefined;
      const utilityId = selectedPilot.projectId?.toString() || '';
      
      if (!utilityId) {
        setRatePlansError('Utility ID is missing');
        setRatePlansOptions([]);
        setRatePlansLoading(false);
        return;
      }
      
      // Call the API endpoint with utility ID
      const response = await rc.bffClient.fetchRatePlans(apiHost, utilityId);
      
      // Check if response has error property (from catch block)
      if ('error' in response && response.error) {
        setRatePlansError('Failed to fetch rate plans');
        setRatePlansOptions([]);
        return;
      }

      // Expected response structure:
      // {
      //   api_host: "...",
      //   data: {
      //     error: null,
      //     payload: [...]
      //   }
      // }
      // Since fetchRatePlans returns axios response, the actual data is in response.data
      // Type guard: if response has 'data' property, it's an AxiosResponse
      const responseData = ('data' in response && response.data) ? response.data : null;
      
      if (!responseData) {
        setRatePlansError('Invalid response structure');
        setRatePlansOptions([]);
        return;
      }
      
      // Check for error in response data
      if (responseData?.data?.error) {
        setRatePlansError(responseData.data.error || 'Failed to fetch rate plans');
        setRatePlansOptions([]);
        return;
      }

      // Extract rate plans from the expected structure: responseData.data.payload
      let ratePlans: RatePlan[] = [];
      
      if (responseData?.data?.payload && Array.isArray(responseData.data.payload)) {
        ratePlans = responseData.data.payload.map((plan: any) => {
          const planNumber = plan.planNumber || plan.number || '';
          const planName = plan.planName || plan.name || planNumber || String(plan);
          return {
            planNumber,
            planName
          };
        }).filter((plan: RatePlan) => plan.planNumber && plan.planName); // Filter out invalid plans
        setRatePlansOptions(ratePlans);
      }
      
    } catch (error) {
      rc.error(rc.getName(DCXClusterCreatePage), 
        `Error fetching rate plans:`, error);
      setRatePlansError('Failed to fetch rate plans');
      setRatePlansOptions([]);
    } finally {
      setRatePlansLoading(false);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
    setSaveAttributeError(null);
    // Fetch rate plans when modal opens if evaluator type is rate
    if (evaluatorType === EVALUATOR_TYPE.RATE) {
      fetchRatePlans();
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    // Reset form state
    setAttributeName('');
    setFuelType('');
    setEvaluatorType(EVALUATOR_TYPE.RATE);
    setDays(30);
    setSelectedRatePlans([]);
    setSaveAttributeError(null);
  };

  const handleAddAttribute = async () => {
    // Validate form
    if (!attributeName.trim()) {
      return;
    }
    if (!fuelType) {
      return;
    }
    if (evaluatorType === EVALUATOR_TYPE.RATE && selectedRatePlans.length === 0) {
      return;
    }
    if (evaluatorType === EVALUATOR_TYPE.BI_MONTHLY && (days < 30 || days > 60)) {
      return;
    }

    // Get projectId from selected pilot
    if (!selectedPilot || !selectedPilot.projectId) {
      setSaveAttributeError('Please select a utility/pilot to create attribute');
      return;
    }

    const projectId = selectedPilot.projectId.toString();

    try {
      setSavingAttribute(true);
      setSaveAttributeError(null);

      // Map fuel type to measurement type
      const measurementTypeMap: Record<string, string> = {
        'Electric': 'ELECTRIC',
        'Gas': 'GAS',
        'Water': 'WATER',
      };
      const measurementType = measurementTypeMap[fuelType] || fuelType.toUpperCase();

      let requestData: any;

      if (evaluatorType === EVALUATOR_TYPE.RATE) {
        // Format for RATE_EVALUATOR
        const ratePlanNumbers = selectedRatePlans.map(plan => parseInt(plan.planNumber, 10)).filter(num => !isNaN(num));
        const attributeId = `PILOT.${projectId}.RATE_MATCH`;
        const attributeValue = JSON.stringify({
          measurementType,
          ratePlanNumbers
        });

        requestData = {
          projectId,
          entityName: 'PILOT',
          attributeName: attributeName.trim(),
          attributeId,
          attributeValue,
          userAttributeEvaluatorType: 'RATE_EVALUATOR',
          description: `User on specified plans`
        };
      } else {
        // Format for BI_MONTHLY_EVALUATOR
        const attributeId = `PILOT.${projectId}.BI_MONTHLY_${days}D`;
        const attributeValue = JSON.stringify({
          minNumDaysInLastBillingCycle: days,
          measurementType
        });

        requestData = {
          projectId,
          entityName: 'PILOT',
          attributeName: attributeName.trim(),
          attributeId,
          attributeValue,
          userAttributeEvaluatorType: 'BI_MONTHLY_EVALUATOR',
          description: `Billing cycle >= ${days} days`
        };
      }

      // Call the API
      const response = await ClusterApis.createAttributes(rc, requestData);

      // Handle response - check if it's an error object
      if (!response.success || response.error) {
        const errorMessage = response.error?.message || response.error || 'Failed to create attribute. Please try again.';
        setSaveAttributeError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      // Extract response data
      const responseData = response.data;
      
      if (!responseData) {
        const errorMessage = 'Invalid response from server';
        setSaveAttributeError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      // Check for error in response data (API returns error in response body)
      if (responseData.error || !responseData.success) {
        const errorMessage = responseData.error || 'Failed to create attribute. Please try again.';
        setSaveAttributeError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      // Success response
      if (responseData.success) {
        const successMessage = responseData.message || 'Attribute created successfully';
        toast.success(successMessage);
        handleCloseModal();
      } else {
        const errorMessage = 'Failed to create attribute. Please try again.';
        setSaveAttributeError(errorMessage);
        toast.error(errorMessage);
      }
      
    } catch (error: any) {
      rc.error(rc.getName(DCXClusterCreatePage), 
        `Error creating attribute:`, error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to create attribute. Please try again.';
      setSaveAttributeError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSavingAttribute(false);
    }
  };

  // Determine loading message dynamically
  const loadingMessage = savingAttribute 
    ? 'Creating attribute...' 
    : isLoading 
    ? 'Creating cluster...' 
    : 'Loading...';

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 3,
      position: 'relative',
    }}>
      {/* Loading Overlay for whole page */}
      <BglBackdropLoader 
        open={isLoading || savingAttribute}
        message={loadingMessage}
        backgroundColor="rgba(255, 255, 255, 0.8)"
        zIndex={1400}
      />
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {isEditMode ? `Edit Cluster: ${clusterName || 'Cluster'}` : 'Create New Cluster'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={handleBack}
            sx={{ color: 'text.secondary',borderColor: 'divider' }}
          >
            Back to Cluster Configuration
          </Button>
        </Box>
      </Box>

      {/* Main Form */}
      <Box sx={{ maxWidth: 800, mx: 'auto',textAlign:'left' }}>
        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {isEditMode ? 'Edit Cluster' : 'Cluster Details'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              {isEditMode 
                ? 'Update the cluster name, category, and definition.' 
                : 'Provide a name and category, then select the cluster type and provide its definition.'}
            </Typography>

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Cluster Name and Cluster Reference - Side by Side */}
            <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
            {/* Cluster Name */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Cluster Name
              </Typography>
                  <Tooltip title="Enter a descriptive name for the cluster" arrow>
                    <Info size={16} style={{ color: theme.palette.text.secondary, cursor: 'help' }} />
                  </Tooltip>
                </Box>
              <TextField
                fullWidth
                value={clusterName}
                onChange={(e) => setClusterName(e.target.value)}
                  placeholder="e.g., High-Propensity EV Adopters"
                size="small"
              />
              </Box>

              {/* Cluster Reference */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Cluster Reference
                  </Typography>
                  <Tooltip title="Enter a reference identifier using only letters, numbers, and underscores. No spaces or special characters allowed." arrow>
                    <Info size={16} style={{ color: theme.palette.text.secondary, cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  value={clusterReference}
                  onChange={(e) => {
                    if (!isEditMode) {
                      const value = e.target.value;
                      // Only allow alphanumeric characters and underscores, no spaces
                      const filteredValue = value.replace(/[^a-zA-Z0-9_]/g, '');
                      setClusterReference(filteredValue);
                    }
                  }}
                  placeholder="e.g., High_Propensity_EV_Adopters"
                  size="small"
                  disabled={isEditMode}
                  helperText={
                    isEditMode 
                      ? "Cluster reference cannot be changed"
                      : clusterReference.includes(' ') 
                      ? "Cluster reference cannot contain spaces" 
                      : !/^[a-zA-Z0-9_]*$/.test(clusterReference)
                      ? "Only letters, numbers, and underscores are allowed"
                      : ""
                  }
                  error={!isEditMode && (clusterReference.includes(' ') || (!/^[a-zA-Z0-9_]*$/.test(clusterReference) && clusterReference.length > 0))}
                />
              </Box>
            </Box>

            {/* Cluster Category */}
            <Box sx={{ mb: 4,textAlign:'left' }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Cluster Category
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap sx={{justifyContent:'space-between',width:'100%',alignItems:'center' }}>
                {categoryOptions.map((category) => (
                  <CategoryChip
                    tokens={tokens}
                    key={category.id}
                    selected={selectedCategories.includes(category.id)}
                    onClick={() => {
                      setSelectedCategories(prev => {
                        if (prev.includes(category.id)) {
                          // Remove if already selected (but keep at least one)
                          if (prev.length > 1) {
                            return prev.filter(id => id !== category.id);
                          }
                          return prev; // Keep at least one selected
                        } else {
                          // Add to selection
                          return [...prev, category.id];
                        }
                      });
                    }}
                    sx={{ mb: 1,width:'100%', }}
                  >
                    <Box style={{display:'flex',alignItems:'center',gap:1,padding:'4px 8px'}}>
                      {category.icon}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: selectedCategories.includes(category.id) ? theme.palette.primary.contrastText : theme.palette.secondary.contrastText,
                        }}
                      >{category.label}</Typography>
                    </Box>
                    <Tooltip title={category.description} arrow>
                      <Info size={14} style={{color: selectedCategories.includes(category.id) ? theme.palette.primary.contrastText : theme.palette.secondary.contrastText }} />
                    </Tooltip>
                    </CategoryChip>
                ))}
              </Stack>
            </Box>

            {/* Cluster Type */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <FolderKanban size={20} style={{marginRight:8}} />
                Cluster Type
              </Typography>
              <FormControl component="fieldset" disabled={isEditMode}>
                <RadioGroup
                  value={clusterType}
                  onChange={(e) => {
                    if (!isEditMode) {
                      setClusterType(e.target.value as 'physical' | 'virtual');
                    }
                  }}
                  row
                >
                  <FormControlLabel
                    value="physical"
                    control={<Radio />}
                    label="Physical (File Upload)"
                    sx={{ mr: 4 }}
                  />
                  <FormControlLabel
                    value="virtual"
                    control={<Radio />}
                    label="Virtual (Rule-Based)"
                  />
                </RadioGroup>
                {isEditMode && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Cluster type cannot be changed after creation
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Physical Cluster - File Upload */}
            {clusterType === 'physical' && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                  Upload UUID File
                </Typography>
                <UploadArea onClick={() => document.getElementById('file-upload')?.click()}>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv,.json,.txt"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <Upload 
                    size={30} 
                    style={{ 
                      color: theme.palette.text.secondary, 
                      marginBottom: theme.spacing(2) 
                    }} 
                  />
                  <Typography variant="body1" sx={{ mb: 0.5, textAlign: 'center' }}>
                    <Box component="span" sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}>
                      Click to upload
                    </Box>
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {' '}or drag and drop
                    </Box>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    CSV, JSON, or TXT file
                  </Typography>
                  {fileValidationError && (
                    <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                      {fileValidationError}
                    </Alert>
                  )}
                </UploadArea>
                
                {/* Selected File Display */}
                {uploadedFile && !fileValidationError && (
                    <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Selected file:{' '}
                      <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                        {uploadedFile.name}
                      </Box>
                    </Typography>
                    </Box>
                  )}
              </Box>
            )}

            {/* Virtual Cluster - Rule Builder */}
            {clusterType === 'virtual' && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                 
                  <Typography variant="h6" sx={{ fontWeight: 500,display: 'flex', alignItems: 'center' }}>
                  <ListTree size={20} style={{marginRight:8}} />
                    Creation Rule Builder
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Plus size={16} />}
                    onClick={handleOpenModal}
                    sx={{ 
                      color: 'text.secondary',
                      borderColor: 'divider',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: 'primary.main',
                      }
                    }}
                  >
                    Add Custom Attribute
                  </Button>
                </Box>
                
                <RuleBuilderArea>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Pilcrow size={16} />}
                      onClick={() => handleAddRule()}
                      size="small"
                      sx={{ 
                        color: 'text.secondary',
                        borderColor: 'divider',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'primary.main',
                        }
                      }}
                    >
                      Add Rule
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Combine size={16} />}
                      onClick={() => handleAddGroup()}
                      size="small"
                      sx={{ 
                        color: 'text.secondary',
                        borderColor: 'divider',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: 'primary.main',
                        }
                      }}
                    >
                      Add Group
                    </Button>
                  </Box>
                  
                  {rules.length === 0 ? (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 4,
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                    }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        This group is empty. Add a rule to begin.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      {rules.map((item, index) => (
                        <RuleItemComponent
                          key={item.id}
                          item={item}
                          index={index}
                          items={rules}
                          clusters={clusters}
                          attributes={attributes}
                          clustersLoading={clustersLoading}
                          attributesLoading={attributesLoading}
                          onUpdateRule={(updates) => handleUpdateRule(index, updates)}
                          onUpdateGroup={(updates) => handleUpdateGroup(index, updates)}
                          onDelete={() => handleDeleteItem(index)}
                          onAddRule={() => {
                            const newRule: RuleItem = {
                              id: `rule-${Date.now()}`,
                              type: 'rule',
                              ruleType: 'attribute',
                              value: '',
                            };
                            const updatedItems = [...rules];
                            updatedItems[index] = {
                              ...(updatedItems[index] as GroupItem),
                              items: [...(updatedItems[index] as GroupItem).items, newRule],
                            };
                            setRules(updatedItems);
                          }}
                          onAddGroup={() => {
                            const newGroup: GroupItem = {
                              id: `group-${Date.now()}`,
                              type: 'group',
                              items: [],
                            };
                            const updatedItems = [...rules];
                            updatedItems[index] = {
                              ...(updatedItems[index] as GroupItem),
                              items: [...(updatedItems[index] as GroupItem).items, newGroup],
                            };
                            setRules(updatedItems);
                          }}
                          onError={(message) => toast.error(message)}
                        />
                      ))}
                    </Box>
                  )}
                </RuleBuilderArea>

                {/* Generated Rule Logic */}
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Info size={16} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Generated Rule Logic
                    </Typography>
                  </Box>
                  <Paper sx={{ p: 2, bgcolor: 'background.default', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {rules.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No rules defined yet.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        {generateRuleLogic(rules)}
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 4 }} />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              {clusterType === 'physical' && uploadedFile && !fileValidationError && (
                <Button
                  variant="outlined"
                  onClick={() => setShowUserCount(!showUserCount)}
                  sx={{ 
                    minWidth: showUserCount ? 200 : 180,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: showUserCount ? theme.palette.primary.main + '15' : 'transparent',
                    borderColor: showUserCount ? theme.palette.primary.main : theme.palette.divider,
                    color: showUserCount ? theme.palette.primary.main : theme.palette.text.secondary,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: theme.palette.primary.main + '10',
                    }
                  }}
                >
                  <Users size={16} />
                  {showUserCount ? (
                    <Typography component="span" sx={{ fontWeight: 500, color: 'inherit' }}>
                      Count: {uploadedUUIDs.length.toLocaleString()}
                    </Typography>
                  ) : (
                    'Check Users Count'
                  )}
                </Button>
              )}

              <BglPrimaryButton
                startIcon={<Plus size={16} />}
                onClick={handleSubmit}
                disabled={isLoading || !clusterName.trim()}
                sx={{ minWidth: 160, ml: clusterType === 'physical' && uploadedFile && !fileValidationError ? 0 : 'auto' }}
              >
                {isLoading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Cluster')}
              </BglPrimaryButton>
            </Box>
          </CardContent>
        </StyledCard>
      </Box>

      {/* Add New Custom Attribute Modal */}
      <Dialog
        open={modalOpen}
        onClose={savingAttribute ? undefined : handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            position: 'relative',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: .5,
            px: 3,
            pt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add New Custom Attribute
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: 3, pb: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', mb: 3 }}
          >
            Define a new attribute and its evaluation logic. This attribute will
            then be available for selection in the rule builder.
          </Typography>

          {/* Attribute Name */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Attribute Name
            </Typography>
            <TextField
              fullWidth
              value={attributeName}
              onChange={(e) => setAttributeName(e.target.value)}
              placeholder="e.g., High-Summer-Usage-Rate"
              size="small"
            />
          </Box>

          {/* Fuel Type */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Fuel Type
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={fuelType}
                onChange={(e) => setFuelType(e.target.value)}
                displayEmpty
                sx={{
                  '& .MuiSelect-select': {
                    color: fuelType ? 'text.primary' : 'text.secondary',
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select a fuel type...
                </MenuItem>
                {fuelTypeOptions.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Evaluator Type */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500, color: 'text.primary' }}>
              Evaluator Type
            </Typography>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                value={evaluatorType}
                onChange={(e) => {
                  const newType = e.target.value as EvaluatorType;
                  setEvaluatorType(newType);
                  // Fetch rate plans when switching to rate evaluator
                  if (newType === EVALUATOR_TYPE.RATE && ratePlansOptions.length === 0 && !ratePlansLoading) {
                    fetchRatePlans();
                  }
                }}
                row
                sx={{ gap: 1.5, width: '100%' }}
              >
                <Box
                  onClick={() => {
                    setEvaluatorType(EVALUATOR_TYPE.RATE);
                    if (ratePlansOptions.length === 0 && !ratePlansLoading) {
                      fetchRatePlans();
                    }
                  }}
                  sx={{
                    flex: 1,
                    border: `1px solid ${
                      evaluatorType === EVALUATOR_TYPE.RATE 
                        ? theme.palette.primary.main 
                        : theme.palette.divider
                    }`,
                    borderRadius: 1.5,
                    p: 0,
                    cursor: 'pointer',
                    bgcolor: evaluatorType === EVALUATOR_TYPE.RATE 
                      ? (mode === 'dark' 
                          ? 'rgba(0, 188, 212, 0.15)' 
                          : 'rgba(0, 188, 212, 0.08)')
                      : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: mode === 'dark' 
                        ? 'rgba(0, 188, 212, 0.1)' 
                        : 'rgba(0, 188, 212, 0.05)',
                    },
                  }}
              >
                <FormControlLabel
                  value={EVALUATOR_TYPE.RATE}
                    control={
                      <Radio 
                        sx={{
                          color: theme.palette.primary.main,
                          p: 0,
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: 'text.primary', fontSize: '0.875rem',ml:.5 }}>
                        RATE Evaluator
                      </Typography>
                    }
                    sx={{ 
                      m: 0,
                      p: 1,
                      '& .MuiFormControlLabel-label': {
                        padding: 0,
                        marginLeft: 1,
                      },
                    }}
                  />
                </Box>
                <Box
                  onClick={() => setEvaluatorType(EVALUATOR_TYPE.BI_MONTHLY)}
                  sx={{
                    flex: 1,
                    border: `1px solid ${
                      evaluatorType === EVALUATOR_TYPE.BI_MONTHLY 
                        ? theme.palette.primary.main 
                        : theme.palette.divider
                    }`,
                    borderRadius: 1.5,
                    p: 0,
                    cursor: 'pointer',
                    bgcolor: evaluatorType === EVALUATOR_TYPE.BI_MONTHLY 
                      ? (mode === 'dark' 
                          ? 'rgba(0, 188, 212, 0.15)' 
                          : 'rgba(0, 188, 212, 0.08)')
                      : 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: mode === 'dark' 
                        ? 'rgba(0, 188, 212, 0.1)' 
                        : 'rgba(0, 188, 212, 0.05)',
                    },
                  }}
                >
                <FormControlLabel
                  value={EVALUATOR_TYPE.BI_MONTHLY}
                    control={
                      <Radio 
                        sx={{
                          color: theme.palette.primary.main,
                          p: 0,
                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: 'text.primary', fontSize: '0.875rem',ml:.5  }}>
                        Bi-Monthly Evaluator
                      </Typography>
                    }
                    sx={{ 
                      m: 0,
                      p: 1,
                      '& .MuiFormControlLabel-label': {
                        padding: 0,
                        marginLeft: 1,
                      },
                    }}
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Error Display */}
          {saveAttributeError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {saveAttributeError}
            </Alert>
          )}

          {/* a separater here */}
          <Divider sx={{ m:0,mb:3 }} />

          {/* Conditional Fields */}
          {evaluatorType === EVALUATOR_TYPE.RATE ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Rate Plans
              </Typography>
              {ratePlansLoading ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
                  Loading rate plans...
                </Typography>
              ) : ratePlansError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {ratePlansError}
                </Alert>
              ) : (
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={selectedRatePlans.map(plan => plan.planNumber)}
                    onChange={(e) => {
                      const selectedPlanNumbers = typeof e.target.value === 'string' 
                        ? e.target.value.split(',')
                        : e.target.value;
                      
                      // Map plan numbers back to RatePlan objects
                      const selectedPlans = ratePlansOptions.filter(plan => 
                        selectedPlanNumbers.includes(plan.planNumber)
                      );
                      setSelectedRatePlans(selectedPlans);
                    }}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ color: 'text.secondary' }}>Select rate plans...</Typography>;
                      }
                      // Display plan names by finding the plans from selected plan numbers
                      const selectedPlans = ratePlansOptions.filter(plan => 
                        (selected as string[]).includes(plan.planNumber)
                      );
                      return selectedPlans.map(plan => plan.planName).join(', ');
                    }}
                    sx={{
                      '& .MuiSelect-select': {
                        minHeight: '40px',
                      },
                    }}
                  >
                    {ratePlansOptions.length === 0 ? (
                      <MenuItem disabled>
                        <Typography sx={{ color: 'text.secondary' }}>No rate plans available</Typography>
                      </MenuItem>
                    ) : (
                      ratePlansOptions.map((plan) => {
                        const isSelected = selectedRatePlans.some(
                          selected => selected.planNumber === plan.planNumber
                        );
                        return (
                          <MenuItem key={plan.planNumber} value={plan.planNumber}>
                            <Checkbox checked={isSelected} />
                            <Typography>{plan.planName}</Typography>
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                </FormControl>
              )}
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Days (30-60)
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={days}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 30 && value <= 60) {
                    setDays(value);
                  }
                }}
                inputProps={{ min: 30, max: 60 }}
                size="small"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
          }}
        >
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddAttribute}
            variant="contained"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
            disabled={
              savingAttribute ||
              !attributeName.trim() ||
              !fuelType ||
              (evaluatorType === EVALUATOR_TYPE.RATE && selectedRatePlans.length === 0) ||
              (evaluatorType === EVALUATOR_TYPE.BI_MONTHLY && (days < 30 || days > 60))
            }
          >
            {savingAttribute ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Saving...
                </Typography>
              </Box>
            ) : (
              'Add Attribute'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
